import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Flame, Trophy, Award, Wallet, Loader2, ExternalLink, AlertCircle, CheckCircle2, LogOut } from "lucide-react";
import { WagmiProvider, useAccount, useConnect, useDisconnect } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { wagmiConfig } from "../../web3/wagmiConfig";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { API } from "../../lib/api";

const T = {
  es: {
    title: "🎓 Mi progreso Learn-to-Earn",
    pending: "R₿L pendientes",
    lifetime_earned: "Ganados aprendiendo",
    lifetime_burned: "Quemados al ecosistema",
    chapters: "Capítulos completados",
    daily_progress: "Hoy",
    cooldown: "Próximo capítulo en",
    available: "Disponible",
    achievements: "Logros desbloqueados",
    claim_btn: "Reclamar a mi wallet",
    claiming: "Reclamando…",
    need_wallet: "Conecta tu wallet para reclamar",
    wallet_placeholder: "0x...",
    history: "Historial de claims",
    view_tx: "Ver tx",
    confirm_wallet: "Wallet destino:",
    confirm_amount: "Cantidad:",
    no_pro: "Necesitas PRO para Learn-to-Earn",
    nothing_pending: "Aún no tienes R₿L pendientes. Completa un capítulo para empezar.",
    success: "¡Reclamado!",
    partial: "Parcial (burn falló, reintentando)",
    connect_wallet: "Conecta tu wallet para retirar",
    connect_wallet_sub: "Necesaria para recibir tus R₿L on-chain en Base.",
    connecting: "Conectando…",
    disconnect: "Desconectar",
    connected_as: "Conectado",
    use_other_addr: "Usar otra dirección",
    use_connected_addr: "Usar wallet conectada",
  },
  en: {
    title: "🎓 My Learn-to-Earn progress",
    pending: "Pending R₿L",
    lifetime_earned: "Earned learning",
    lifetime_burned: "Burned to ecosystem",
    chapters: "Chapters completed",
    daily_progress: "Today",
    cooldown: "Next chapter in",
    available: "Available",
    achievements: "Achievements unlocked",
    claim_btn: "Claim to my wallet",
    claiming: "Claiming…",
    need_wallet: "Connect your wallet to claim",
    wallet_placeholder: "0x...",
    history: "Claim history",
    view_tx: "View tx",
    confirm_wallet: "Destination wallet:",
    confirm_amount: "Amount:",
    no_pro: "PRO required for Learn-to-Earn",
    nothing_pending: "No pending R₿L yet. Complete a chapter to start.",
    success: "Claimed!",
    partial: "Partial (burn failed, will retry)",
    connect_wallet: "Connect your wallet to withdraw",
    connect_wallet_sub: "Needed to receive your R₿L on-chain on Base.",
    connecting: "Connecting…",
    disconnect: "Disconnect",
    connected_as: "Connected",
    use_other_addr: "Use another address",
    use_connected_addr: "Use connected wallet",
  },
  sk: {
    title: "🎓 Môj Learn-to-Earn pokrok",
    pending: "R₿L na uplatnenie",
    lifetime_earned: "Zarobené učením",
    lifetime_burned: "Spálené do ekosystému",
    chapters: "Dokončené kapitoly",
    daily_progress: "Dnes",
    cooldown: "Ďalšia kapitola o",
    available: "Dostupné",
    achievements: "Odomknuté úspechy",
    claim_btn: "Uplatniť do peňaženky",
    claiming: "Uplatňujem…",
    need_wallet: "Pripoj peňaženku pre uplatnenie",
    wallet_placeholder: "0x...",
    history: "História claimov",
    view_tx: "Zobraziť tx",
    confirm_wallet: "Cieľová peňaženka:",
    confirm_amount: "Suma:",
    no_pro: "PRO potrebné pre Learn-to-Earn",
    nothing_pending: "Zatiaľ žiadne R₿L. Dokonči kapitolu pre štart.",
    success: "Uplatnené!",
    partial: "Čiastočne (burn zlyhalo, skúsime znova)",
    connect_wallet: "Pripoj peňaženku na výber",
    connect_wallet_sub: "Potrebná na prijatie R₿L on-chain na Base.",
    connecting: "Pripájam…",
    disconnect: "Odpojiť",
    connected_as: "Pripojené",
    use_other_addr: "Použiť inú adresu",
    use_connected_addr: "Použiť pripojenú peňaženku",
  }, de: { title: "🎓 Mein Learn-to-Earn-Fortschritt", pending: "Ausstehende R₿L", lifetime_earned: "Durch Lernen verdient", lifetime_burned: "Ans Ökosystem verbrannt", chapters: "Abgeschlossene Kapitel", daily_progress: "Heute", cooldown: "Nächstes Kapitel in", available: "Verfügbar", achievements: "Freigeschaltete Erfolge", claim_btn: "In meine Wallet abheben", claiming: "Wird abgehoben…", need_wallet: "Verbinde deine Wallet zum Abheben", wallet_placeholder: "0x...", history: "Abhebungsverlauf", view_tx: "Tx ansehen", confirm_wallet: "Ziel-Wallet:", confirm_amount: "Betrag:", no_pro: "Du benötigst PRO für Learn-to-Earn", nothing_pending: "Du hast noch keine ausstehenden R₿L. Schließe ein Kapitel ab, um zu beginnen.", success: "Abgehoben!", partial: "Teilweise (Burn fehlgeschlagen, wird wiederholt)", connect_wallet: "Verbinde deine Wallet zum Abheben", connect_wallet_sub: "Erforderlich, um deine R₿L on-chain auf Base zu erhalten.", connecting: "Verbinde…", disconnect: "Trennen", connected_as: "Verbunden", use_other_addr: "Andere Adresse verwenden", use_connected_addr: "Verbundene Wallet verwenden" }, "zh-CN": { title: "🎓 我的 Learn-to-Earn 进度", pending: "待领取 R₿L", lifetime_earned: "学习获得总计", lifetime_burned: "销毁至生态系统", chapters: "已完成章节", daily_progress: "今日", cooldown: "下一章节可用时间", available: "可用", achievements: "已解锁成就", claim_btn: "领取到我的钱包", claiming: "领取中…", need_wallet: "连接钱包以领取", wallet_placeholder: "0x...", history: "领取历史", view_tx: "查看交易", confirm_wallet: "目标钱包：", confirm_amount: "数量：", no_pro: "需要 PRO 才能使用 Learn-to-Earn", nothing_pending: "你还没有待领取的 R₿L。完成一个章节即可开始。", success: "领取成功！", partial: "部分成功（销毁失败，正在重试）", connect_wallet: "连接钱包以提取", connect_wallet_sub: "需要在 Base 链上接收你的 R₿L。", connecting: "连接中…", disconnect: "断开连接", connected_as: "已连接", use_other_addr: "使用其他地址", use_connected_addr: "使用已连接钱包" },
};

const ACH_LABEL = {
  daily_student: { es: "Estudiante del día", en: "Daily student", sk: "Študent dňa", de: "Student des Tages", "zh-CN": "今日学员" },
  perfect_week: { es: "Semana perfecta", en: "Perfect week", sk: "Perfektný týždeň", de: "Perfekte Woche", "zh-CN": "完美的一周" },
  quiz_master: { es: "Maestro del quiz", en: "Quiz master", sk: "Majster kvízu", de: "Quiz-Meister", "zh-CN": "问答大师" },
  module_complete: { es: "Maratonista", en: "Module marathon", sk: "Maratónec modulu", de: "Marathonläufer", "zh-CN": "马拉松跑者" },
};

const fmt = (n) => Number(n || 0).toLocaleString("es-ES");

const isValidAddr = (a) => /^0x[0-9a-fA-F]{40}$/.test(String(a || "").trim());

const useCooldown = (cooldownUntilIso) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!cooldownUntilIso) return null;
  const target = new Date(cooldownUntilIso).getTime();
  const secs = Math.max(0, Math.floor((target - now) / 1000));
  return secs;
};

const lpQueryClient = new QueryClient();

export const LearningProgress = ({ defaultWallet = "" }) => (
  <WagmiProvider config={wagmiConfig}>
    <QueryClientProvider client={lpQueryClient}>
      <LearningProgressInner defaultWallet={defaultWallet} />
    </QueryClientProvider>
  </WagmiProvider>
);

const LearningProgressInner = ({ defaultWallet = "" }) => {
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;

  const { address: connectedAddress, isConnected } = useAccount();
  const { connectors, connect, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [wallet, setWallet] = useState(defaultWallet);
  const [manualOverride, setManualOverride] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [error, setError] = useState(null);

  // Auto-sync the wallet input with the connected wallet (unless the user
  // explicitly switched to manual mode to claim to a different address).
  useEffect(() => {
    if (isConnected && connectedAddress && !manualOverride) {
      setWallet(connectedAddress);
    }
  }, [isConnected, connectedAddress, manualOverride]);

  const authedFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opts.headers || {}),
      },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const e = new Error(body?.detail?.code || body?.detail || res.statusText);
      e.detail = body?.detail; e.status = res.status;
      throw e;
    }
    return body;
  }, [token]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const [p, c] = await Promise.all([
        authedFetch("/learn/progress"),
        authedFetch("/learn/claims"),
      ]);
      setData(p);
      setClaims(c.claims || []);
    } catch (e) {
      setError(String(e.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [token, authedFetch]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (defaultWallet) setWallet(defaultWallet);
  }, [defaultWallet]);

  const handleClaim = async () => {
    if (!isValidAddr(wallet) || claiming) return;
    setClaiming(true);
    setError(null);
    setClaimResult(null);
    try {
      const res = await authedFetch("/learn/claim", {
        method: "POST",
        body: JSON.stringify({ wallet_address: wallet.trim() }),
      });
      setClaimResult(res);
      await refresh();
    } catch (e) {
      const code = typeof e.detail === "object" ? e.detail?.code : (e.detail || e.message);
      setError(String(code || "claim_failed"));
    } finally {
      setClaiming(false);
    }
  };

  const cooldownSecs = useCooldown(data?.cooldown_until);

  if (!user) return null;
  if (!user.is_pro) {
    return (
      <div
        className="rounded-2xl border border-orange-400/30 bg-orange-500/[0.06] p-5 backdrop-blur"
        data-testid="learning-progress-no-pro"
      >
        <div className="flex items-center gap-2 text-sm text-orange-200">
          <GraduationCap className="h-4 w-4" /> {t.no_pro}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 rounded-2xl border border-[#F5D76E]/25 bg-[#F5D76E]/[0.04] p-5 backdrop-blur"
      data-testid="learning-progress"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-white">{t.title}</h3>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-white/40" />}
      </div>

      {/* Wallet connection (always visible so users can connect any time) */}
      <div className="rounded-xl border border-[#F5D76E]/20 bg-black/40 p-4" data-testid="lp-wallet-connect">
        {isConnected && connectedAddress ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-white/80">
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
              <span className="uppercase tracking-[0.18em] text-white/55">{t.connected_as}:</span>
              <span className="font-mono font-bold text-[#F5D76E]" data-testid="lp-connected-address">
                {connectedAddress.slice(0, 6)}…{connectedAddress.slice(-4)}
              </span>
            </div>
            <button
              onClick={() => { setManualOverride(false); disconnect(); }}
              data-testid="lp-disconnect-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-mono text-[10px] font-black uppercase tracking-[0.15em] text-white/55 transition-colors hover:border-rose-400/40 hover:text-rose-300"
            >
              <LogOut className="h-3 w-3" /> {t.disconnect}
            </button>
          </div>
        ) : (
          <div>
            <p className="flex items-center gap-2 text-xs font-bold text-white">
              <Wallet className="h-3.5 w-3.5 text-[#F5D76E]" /> {t.connect_wallet}
            </p>
            <p className="mt-1 text-[11px] text-white/45">{t.connect_wallet_sub}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {connectors.map((c) => (
                <button
                  key={c.uid}
                  disabled={isConnecting}
                  onClick={() => connect({ connector: c })}
                  data-testid={`lp-connect-${c.id}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-xs font-bold text-white transition-all hover:border-[#F5D76E]/60 disabled:opacity-50"
                >
                  {isConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wallet className="h-3.5 w-3.5 text-[#F5D76E]" />}
                  {c.id === "injected" ? "MetaMask / Browser" : c.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pending balance + claim */}
      <div className="rounded-xl border border-white/10 bg-black/40 p-4">
        <div className="flex items-baseline justify-between">
          <span className="text-xs uppercase tracking-[0.18em] text-white/55">{t.pending}</span>
          <span className="font-mono text-3xl font-black text-[#F5D76E]" data-testid="lp-pending">
            {fmt(data?.pending_balance_rbl || 0)} <span className="text-sm">R₿L</span>
          </span>
        </div>
        {data?.pending_balance_rbl > 0 ? (
          <div className="mt-4 space-y-2">
            <input
              type="text"
              value={wallet}
              onChange={(e) => { setManualOverride(true); setWallet(e.target.value); }}
              placeholder={t.wallet_placeholder}
              className="w-full rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 font-mono text-xs text-white placeholder:text-white/30 focus:border-[#F5D76E]/60 focus:outline-none"
              data-testid="lp-wallet-input"
            />
            {isConnected && connectedAddress && (
              <button
                type="button"
                onClick={() => {
                  if (manualOverride) {
                    setManualOverride(false);
                    setWallet(connectedAddress);
                  } else {
                    setManualOverride(true);
                  }
                }}
                data-testid="lp-toggle-wallet-source"
                className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#F5D76E]/80 hover:text-[#F5D76E]"
              >
                {manualOverride ? t.use_connected_addr : t.use_other_addr}
              </button>
            )}
            <button
              onClick={handleClaim}
              disabled={!isValidAddr(wallet) || claiming || !data?.pending_balance_rbl}
              className="w-full rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2.5 text-xs font-black uppercase tracking-[0.2em] text-black transition hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,140,0,0.5)] disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="lp-claim-btn"
            >
              {claiming ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.claiming}</span>
              ) : (
                <span className="inline-flex items-center gap-2"><Wallet className="h-3.5 w-3.5" /> {t.claim_btn}</span>
              )}
            </button>
            {error && (
              <p className="flex items-center gap-1.5 text-[11px] text-red-300/90" data-testid="lp-claim-error">
                <AlertCircle className="h-3 w-3" /> {error}
              </p>
            )}
            {claimResult && (
              <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/[0.08] p-3 text-xs" data-testid="lp-claim-success">
                <p className="flex items-center gap-1.5 font-bold text-emerald-300">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {claimResult.status === "partial" ? t.partial : t.success}
                </p>
                {claimResult.tx_hash && (
                  <a
                    href={claimResult.explorer_url}
                    target="_blank" rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] text-emerald-200/80 hover:text-emerald-200 hover:underline"
                  >
                    {claimResult.tx_hash.slice(0, 10)}…{claimResult.tx_hash.slice(-6)}
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="mt-2 text-[11px] text-white/50">{t.nothing_pending}</p>
        )}
      </div>

      {/* Lifetime + daily */}
      <div className="grid grid-cols-2 gap-3">
        <Stat icon={<Trophy className="h-3.5 w-3.5" />} color="#F5D76E"
              label={t.lifetime_earned} value={`${fmt(data?.lifetime?.total_earned)} R₿L`}
              testid="lp-lifetime-earned" />
        <Stat icon={<Flame className="h-3.5 w-3.5" />} color="#f97316"
              label={t.lifetime_burned} value={`${fmt(data?.lifetime?.total_burned)} R₿L`}
              testid="lp-lifetime-burned" />
        <Stat icon={<GraduationCap className="h-3.5 w-3.5" />} color="#A855F7"
              label={t.chapters} value={fmt(data?.lifetime?.chapters_completed)}
              testid="lp-chapters" />
        <Stat icon={<Wallet className="h-3.5 w-3.5" />} color="#00E5FF"
              label={t.daily_progress}
              value={`${fmt(data?.daily?.rbl_earned)} / ${fmt(data?.daily?.rbl_limit)}`}
              testid="lp-daily" />
      </div>

      {cooldownSecs > 0 && (
        <p className="text-center font-mono text-[11px] text-white/55" data-testid="lp-cooldown">
          ⏱ {t.cooldown} {Math.floor(cooldownSecs / 60)}:{String(cooldownSecs % 60).padStart(2, "0")}
        </p>
      )}

      {data?.achievements?.length > 0 && (
        <div className="rounded-xl border border-violet-400/25 bg-violet-500/[0.06] p-3" data-testid="lp-achievements">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-300">
            ✨ {t.achievements}
          </p>
          <ul className="mt-2 space-y-1">
            {data.achievements.slice(0, 6).map((a) => (
              <li key={a.key} className="flex items-center gap-2 text-xs text-white" data-testid={`lp-ach-${a.code}`}>
                <Award className="h-3.5 w-3.5 text-violet-300" />
                <span className="flex-1">{ACH_LABEL[a.code]?.[lang] || a.code}</span>
                <span className="font-mono text-[10px] text-violet-200">+{fmt(a.bonus_rbl)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {claims.length > 0 && (
        <div data-testid="lp-history">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55">{t.history}</p>
          <ul className="mt-2 space-y-1.5">
            {claims.slice(0, 5).map((c) => (
              <li key={c.claim_id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                <span className="font-mono text-white/70">{fmt(c.student_rbl)} R₿L</span>
                <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/55">
                  {c.status}
                </span>
                {c.tx_hash && (
                  <a href={c.explorer_url} target="_blank" rel="noreferrer"
                     className="ml-2 inline-flex items-center gap-1 text-[10px] text-[#F5D76E] hover:underline">
                    {t.view_tx} <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
};

const Stat = ({ icon, label, value, color, testid }) => (
  <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2.5" data-testid={testid}>
    <div className="flex items-center gap-1.5" style={{ color }}>
      {icon}
      <span className="text-[10px] uppercase tracking-[0.15em] text-white/55">{label}</span>
    </div>
    <p className="mt-1 font-mono text-sm font-bold text-white">{value}</p>
  </div>
);

export default LearningProgress;
